import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import {
  SATU_EVENT_TRACKER_EVENT_NAME,
  SATU_EVENT_DEFAULT_EVENT_TYPE,
} from 'utils/satu-event-tracker/config/event-tracking'
import { useSatuEventTracker } from './useSatuEventTracker'

interface PageViewAttributes {
  screen_name: string
  referrer?: string
  is_logged_in?: [boolean, 'bool']
  is_webview?: [boolean, 'bool']
  [key: string]: any
}

interface UsePageViewTrackerOptions {
  activityType: string
  eventName?: string
  screenName: string
  attributes?: Omit<PageViewAttributes, 'screen_name'>
  enabled?: boolean
}

/**
 * Custom hook to track page views with SatuEvent tracker.
 * Tracks page views on route changes and initial mount, preventing duplicate events
 * during re-renders. Works correctly with both regular reloads and Fast Refresh.
 *
 * @example
 * ```tsx
 * usePageViewTracker({
 *   screenName: 'ide_praktik_video_inspirasi',
 *   attributes: {
 *     referrer: referrer || 'direct',
 *     is_logged_in: [Boolean(session), 'bool'],
 *     is_webview: [androidHandler.enabled, 'bool'],
 *   },
 * })
 * ```
 */
export const usePageViewTracker = ({
  activityType,
  eventName = SATU_EVENT_DEFAULT_EVENT_TYPE,
  screenName,
  attributes = {},
  enabled = true,
}: UsePageViewTrackerOptions) => {
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()
  const router = useRouter()
  const lastTrackedPath = useRef<string>('')

  useEffect(() => {
    if (!enabled) return

    const currentPath = router.asPath

    // Only track if the path has changed or this is the first navigation
    if (lastTrackedPath.current === currentPath) return

    SETrackEvent({
      eventName: SATU_EVENT_TRACKER_EVENT_NAME,
      eventParams: {
        activity_type: activityType,
        event_name: eventName,
        attributes: SETransformAttrs({
          screen_name: screenName,
          ...attributes,
        }),
      },
    })

    lastTrackedPath.current = currentPath
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, screenName, enabled]) // Track on path changes
}
